/**
 * One day in the weekly order view — a collapsible card (Chefs-app pattern).
 *
 * Collapsed: date pill + weekday (+ a blue dot when the day has un-added
 * draft selections) + a CTA pill driven by `cta` (see
 * `lib/school-orders/day-cta.ts`): "Show details" for days with orders,
 * "Order now" for orderable days with nothing placed (primary blue when
 * `ctaPrimary` — the week's suggested starting point), or nothing at all —
 * a fully inert card — when there's nothing to see or do (no active menu,
 * or closed with nothing ordered). Below the header, a per-mealtime summary
 * of meals already ordered per grade column — grade badges tint GREEN where
 * they have orders and gray when zero. With many grade columns the summary
 * stacks one mealtime per row instead of laying them inline.
 *
 * Mobile (`href` set) uses a compact variant of that header: a smaller date
 * pill and weekday, tighter padding, and the summary on a second line beside
 * the weekday rather than in a block of its own.
 *
 * Expanded: the summary is replaced by the `DayOrderPanel` (location tabs +
 * stacked mealtime tables + "Add meals to cart"). The header sticks while the
 * expanded body scrolls, matching the kitchen order cards.
 *
 * History mode (PROD-1123 v2, `cta === 'service-details'`): past/completed
 * days show read-only navigation to orders and service details, with
 * a service badge ("Service complete" / "Service not completed") and
 * separate "View order" and "View service details" links on every breakpoint.
 *
 * Read-only surfaces (org SCHOOL_ORDER channel off, or a PARENT-only campus)
 * add a "+X parent orders" line under the summary for any day parents ordered
 * — past, today or future (FACTORY-554). Such a campus places no school
 * batches, so the grade badges read zero and parent demand is the content.
 */

import { Badge } from '@ordo/ui/badge'
import { cn } from '@ordo/ui/cn'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { type ReactElement, useMemo } from 'react'
import { Link } from 'react-router'

import { useMenuWeekPreviewQuery } from '@/api/orders'
import { type AdminSchool } from '@/api/school-orders'
import { dayMenuTeaser, OnTheMenuStrip } from '@/components/orders/on-the-menu-strip'
import type { AlreadyOrdered } from '@/lib/school-orders/already-ordered'
import { dateFromKey, mondayOf } from '@/lib/school-orders/calendar'
import { sameDayLines } from '@/lib/school-orders/cart'
import type { DayCtaState } from '@/lib/school-orders/day-cta'
import { orderClosedReason, schoolDeadlinePolicy } from '@/lib/school-orders/deadline'
import { selectLinesFor, useSchoolCart } from '@/stores/school-cart'
import { draftLinesForDay, selectDraftMapFor, useSchoolOrderDraft } from '@/stores/school-order-draft'

import { CollapsedSummary } from './collapsed-summary'
import { DayOrderPanel } from './day-order-panel'
import { parentOrdersLegend } from './day-order-table'

export interface DayCardProps {
  school: AdminSchool
  dateKey: string
  alreadyOrdered: AlreadyOrdered
  /** Forwarded to DayOrderPanel — placed-orders queries still loading. */
  ordersPending?: boolean
  readOnly?: boolean
  /**
   * What the header affords (computed per day in `WeekOrdersView`): the
   * "Show details" / "Order now" pill copy, or 'hidden' — no pill and an
   * inert, non-clickable card.
   */
  cta: DayCtaState
  /** Render the "Order now" pill as the primary (blue) CTA — the week's suggested starting point. */
  ctaPrimary?: boolean
  /** Controlled: expanded turns the card into an in-place full-height "page". */
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * When set, the card header is a navigation link to this URL (mobile) — a
   * right chevron replaces the "Show details" expand control and the card
   * never expands in place. When omitted, the card expands in place (desktop).
   */
  href?: string
  /** Cafeteria "Orders lookup": view-only combined totals (see `DayOrderPanel`). */
  lookahead?: boolean
  /**
   * The school enabled parent ordering of the field trip meal this day
   * (field-trip parent-order election). `fieldTripOnly` = every other menu
   * item is disabled for the in-scope students.
   */
  parentFieldTrip?: { fieldTripOnly: boolean } | null
  /**
   * History mode (PROD-1123 v2, `cta === 'service-details'`): the "View
   * service details" destination. Required for the history render paths.
   */
  serviceDetailsHref?: string
  /** History mode: the day's cafeteria service-completion rollup verdict. */
  serviceComplete?: boolean
  /** The school marked the day NO_SERVICE — the summary says so (PROD-1228). */
  noService?: boolean
  /** All the admin's schools — feeds the panel's location tab strip. */
  schools?: AdminSchool[]
  /** Select another campus from the panel's location tab strip. */
  onSelectSchool?: (locationId: string) => void
}

export function DayCard({
  school,
  dateKey,
  alreadyOrdered,
  ordersPending,
  readOnly = false,
  cta,
  ctaPrimary = false,
  open,
  onOpenChange,
  href,
  lookahead = false,
  parentFieldTrip = null,
  serviceDetailsHref,
  serviceComplete = false,
  noService = false,
  schools,
  onSelectSchool,
}: DayCardProps): ReactElement {
  const date = dateFromKey(dateKey)
  const monthAbbr = date.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }).toUpperCase()
  const dayNum = date.getUTCDate()
  const weekday = date.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' })

  // Blue dot: the day's draft differs from its committed cart lines. The
  // draft now persists after "Add meals to cart" (it mirrors the cart), so
  // a raw draft-count check would flag every committed day forever.
  const draftMap = useSchoolOrderDraft(selectDraftMapFor(school.id))
  const cartLines = useSchoolCart(selectLinesFor(school.id))
  const hasPending = useMemo(
    () =>
      !sameDayLines(
        draftLinesForDay(draftMap, dateKey),
        cartLines.filter((l) => l.date === dateKey),
      ),
    [draftMap, cartLines, dateKey],
  )

  const pendingDot = hasPending ? (
    <span title="Unsaved selections — not yet added to cart" className="inline-flex h-2 w-2 rounded-full bg-primary" />
  ) : null

  // Date pill + weekday — the desktop header lead, shared by the collapsible
  // trigger and the inert card.
  const headerLead = (
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md bg-muted leading-none">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{monthAbbr}</span>
        <span className="text-base font-semibold text-foreground">{dayNum}</span>
      </span>
      <span className="flex items-center gap-2 text-xl font-medium text-foreground">
        {weekday}
        {pendingDot}
        {parentFieldTrip ? (
          <Badge variant="secondary" className="text-xs font-medium">
            {parentFieldTrip.fieldTripOnly ? 'Parents: field trip meal only' : 'Parents: field trip meal'}
          </Badge>
        ) : null}
      </span>
    </div>
  )

  // Lookahead folds parents into the badge totals already, so its cards skip
  // the line; an orderable campus sees parent demand in the expanded table.
  const parentLegend = readOnly && !lookahead ? parentOrdersLegend(alreadyOrdered.familyDayQty(dateKey)) : null
  const summaryCore = (
    <CollapsedSummary
      school={school}
      dateKey={dateKey}
      alreadyOrdered={alreadyOrdered}
      green
      noService={noService && alreadyOrdered.dayQty(dateKey) === 0}
    />
  )
  const summary = parentLegend ? (
    <div className="flex flex-col gap-1.5">
      {summaryCore}
      <p className="text-[11px] leading-none text-muted-foreground">{parentLegend}</p>
    </div>
  ) : (
    summaryCore
  )

  // "On the menu" teaser below the counts — the exact strip the family week
  // card uses, so the day sells its food (bundle photos first) and reads as
  // clickable. Unbanded fetch: the admin sees the campus-wide menu. The five
  // day cards of a week share one cached (location, weekStart) response.
  const { data: weekPreview } = useMenuWeekPreviewQuery({ locationId: school.id, weekStart: mondayOf(dateKey) })
  const teaser = dayMenuTeaser(weekPreview?.days, dateKey)
  const teaserStrip = teaser ? <OnTheMenuStrip preview={teaser} /> : null

  const collapsedSummary = (
    <div className="flex flex-col gap-4 px-4 pb-4">
      {summary}
      {teaserStrip}
    </div>
  )

  // History mode (PROD-1123 v2): past/completed days show a service badge +
  // links to both order review and service details. History
  // cards never expand in place and never show the grade-chip summary.
  const history = cta === 'service-details'
  const serviceBadge = history ? (
    serviceComplete ? (
      <Badge variant="success">Service complete</Badge>
    ) : (
      <Badge variant="secondary" className="text-muted-foreground">
        Service not completed
      </Badge>
    )
  ) : null

  // Nothing to see or do (no active menu, or closed with nothing ordered) —
  // no pill/chevron and the header is not a control. An already-open card
  // keeps its normal header so it can still be collapsed.
  const inert = cta === 'hidden' && !open

  // A closed day the school never ordered reads as one plain "Not ordered"
  // legend instead of the zeroed mealtime × grade badges. A NO_SERVICE day is
  // never "not ordered" (there was nothing to order), so it keeps the summary's
  // "No service — school closed" (PROD-1471).
  const dayQty = alreadyOrdered.dayQty(dateKey)
  const notOrdered =
    inert && dayQty === 0 && !noService && orderClosedReason(dateKey, schoolDeadlinePolicy(school)) !== null
  const inertSummary = notOrdered ? <p className="text-sm text-muted-foreground">Not ordered</p> : summary

  const historyActions = (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <Link
        to={`/orders/school/day?locationId=${encodeURIComponent(school.id)}&date=${encodeURIComponent(dateKey)}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3.5 py-1.5 text-sm font-medium transition-colors hover:bg-muted/70">
        View order
        <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
      </Link>
      {serviceDetailsHref ? (
        <Link
          to={serviceDetailsHref}
          className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3.5 py-1.5 text-sm font-medium transition-colors hover:bg-muted/70">
          View service details
          <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </Link>
      ) : null}
    </div>
  )

  // Mobile: the whole card navigates to the full-page day view; a right chevron
  // signals the jump and the card never expands in place. The header is the
  // compact two-line form — the summary sits with the weekday rather than in a
  // block of its own — so five days plus the Options menu fit one screen.
  if (href) {
    const compactHeader = (
      <div className="flex w-full items-center gap-3 p-3">
        <span className="flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded-md bg-muted leading-none">
          <span className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">{monthAbbr}</span>
          <span className="text-sm font-semibold tabular-nums text-foreground">{dayNum}</span>
        </span>
        {/* `gap-1.5`: at 4px the weekday and its summary crowded each other —
            the summary sets `leading-none`, so it needs the extra 2px to read
            as a second line rather than a collision. */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="flex items-center gap-2 text-base font-semibold text-foreground">
            {weekday}
            {pendingDot}
          </span>
          {/* History cards carry the service badge; everything else keeps
              main's summary + menu teaser (inert days trade the zeroed
              badges for the plain "Not ordered" legend). */}
          {history ? (
            <span className="flex items-center">{serviceBadge}</span>
          ) : (
            <>
              {inert ? inertSummary : summary}
              {teaserStrip}
            </>
          )}
        </div>
        {inert || history ? null : (
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        )}
      </div>
    )
    if (history) {
      return (
        <div className="overflow-hidden rounded-xl border bg-card text-left">
          {compactHeader}
          <div className="px-3 pb-3">{historyActions}</div>
        </div>
      )
    }
    if (inert) {
      return <div className="overflow-hidden rounded-xl border bg-card text-left">{compactHeader}</div>
    }
    return (
      <Link
        to={href}
        className="block overflow-hidden rounded-xl border bg-card text-left transition-colors hover:bg-muted/30 focus:outline-none focus-visible:outline-none">
        {compactHeader}
      </Link>
    )
  }

  // History cards offer both order review and cafeteria service details.
  if (history && serviceDetailsHref) {
    return (
      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="flex w-full flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex min-w-0 items-center gap-3">
            {headerLead}
            {serviceBadge}
          </div>
          {historyActions}
        </div>
      </div>
    )
  }

  if (inert) {
    return (
      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="flex w-full items-center justify-between gap-3 p-4 text-left">{headerLead}</div>
        {/* Same body as `collapsedSummary`, with the zeroed badges traded for
            the "Not ordered" legend — the menu teaser stays (mobile parity). */}
        <div className="flex flex-col gap-4 px-4 pb-4">
          {inertSummary}
          {teaserStrip}
        </div>
      </div>
    )
  }

  // "Order now" (vs "Show details") marks an orderable day with nothing
  // placed; the primary variant is the week's suggested entry point. A day
  // with a placed order says "View order" — the pill reviews, not orders.
  const orderNow = !open && cta === 'order-now'
  const primaryPill = orderNow && ctaPrimary
  let pillLabel = dayQty > 0 ? 'View order' : 'Show details'
  if (open) {
    pillLabel = 'Hide details'
  } else if (orderNow) {
    pillLabel = 'Order now'
  }

  return (
    <div className={cn('overflow-hidden rounded-xl border bg-card', open && 'flex min-h-0 flex-1 flex-col')}>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex w-full shrink-0 cursor-pointer items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-muted/30 focus:outline-none focus-visible:outline-none">
        {headerLead}
        <span
          aria-hidden
          className={cn(
            'inline-flex w-32 shrink-0 items-center justify-between gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium',
            primaryPill ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
          )}>
          {pillLabel}
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              open && 'rotate-180',
              primaryPill ? 'text-primary-foreground/80' : 'text-muted-foreground',
            )}
            strokeWidth={1.5}
          />
        </span>
      </button>

      {open ? (
        // Fills the remaining card height; only the panel's table scrolls.
        <div className="flex min-h-0 flex-1 flex-col">
          <DayOrderPanel
            school={school}
            dateKey={dateKey}
            alreadyOrdered={alreadyOrdered}
            ordersPending={ordersPending}
            readOnly={readOnly}
            lookahead={lookahead}
            schools={schools}
            onSelectSchool={onSelectSchool}
          />
        </div>
      ) : (
        collapsedSummary
      )}
    </div>
  )
}
